import { useState } from 'react';
import { Link } from 'react-router-dom';
import { axios } from '../App';
import { toast } from 'sonner';
import { Radio, Radar, Eye, Volume2, Waves as WavesIcon, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

export default function SensorFusion() {
  const [formData, setFormData] = useState({
    sensor_id: '',
    sensor_type: 'radar',
    domain: 'air',
    detection_confidence: '0.85',
    lat: '38.9072',
    lng: '-77.0369',
    altitude: '',
    speed: '',
    heading: '',
    signal_strength: '',
    frequency: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const sensorTypesByDomain = {
    air: ['radar', 'rf', 'optical'],
    surface: ['radar', 'rf', 'optical'],
    underwater: ['acoustic', 'sonar']
  };

  const sensorIcons = {
    radar: Radar,
    rf: Radio,
    optical: Eye,
    acoustic: Volume2,
    sonar: WavesIcon
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        ...formData,
        detection_confidence: parseFloat(formData.detection_confidence),
        lat: parseFloat(formData.lat),
        lng: parseFloat(formData.lng),
        altitude: formData.altitude ? parseFloat(formData.altitude) : null,
        speed: formData.speed ? parseFloat(formData.speed) : null,
        heading: formData.heading ? parseFloat(formData.heading) : null,
        signal_strength: formData.signal_strength ? parseFloat(formData.signal_strength) : null
      };

      await axios.post('/sensor-readings', payload);
      toast.success(`${formData.sensor_type.toUpperCase()} reading ingested successfully`);
      
      setFormData({
        sensor_id: '',
        sensor_type: formData.sensor_type,
        domain: formData.domain,
        detection_confidence: '0.85',
        lat: '38.9072',
        lng: '-77.0369',
        altitude: '',
        speed: '',
        heading: '',
        signal_strength: '',
        frequency: ''
      });
    } catch (error) {
      toast.error('Failed to ingest sensor reading');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDomainChange = (domain) => {
    const defaultType = sensorTypesByDomain[domain][0];
    setFormData({ ...formData, domain, sensor_type: defaultType });
  };

  const SensorIcon = sensorIcons[formData.sensor_type] || Radar;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase">Sensor Data Fusion</h1>
        <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
          Multi-sensor integration & AI-powered fusion
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-sky-500/10 border border-sky-500/30 rounded-sm flex items-center justify-center">
                <SensorIcon size={24} className="text-sky-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold uppercase">Ingest Sensor Reading</h2>
                <p className="text-xs text-zinc-600 font-mono">Real-time sensor data input</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Domain</Label>
                  <Select value={formData.domain} onValueChange={handleDomainChange}>
                    <SelectTrigger className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="air">Air</SelectItem>
                      <SelectItem value="surface">Surface</SelectItem>
                      <SelectItem value="underwater">Underwater</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Sensor Type</Label>
                  <Select
                    value={formData.sensor_type}
                    onValueChange={(value) => setFormData({ ...formData, sensor_type: value })}
                  >
                    <SelectTrigger className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {sensorTypesByDomain[formData.domain].map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Sensor ID</Label>
                  <Input
                    value={formData.sensor_id}
                    onChange={(e) => setFormData({ ...formData, sensor_id: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    required
                    placeholder="SENSOR-001"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Confidence</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={formData.detection_confidence}
                    onChange={(e) => setFormData({ ...formData, detection_confidence: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    required
                    placeholder="0.85"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Latitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.lat}
                    onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Longitude</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.lng}
                    onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Altitude (m)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.altitude}
                    onChange={(e) => setFormData({ ...formData, altitude: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Speed (km/h)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.speed}
                    onChange={(e) => setFormData({ ...formData, speed: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Heading (°)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.heading}
                    onChange={(e) => setFormData({ ...formData, heading: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Signal Strength</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.signal_strength}
                    onChange={(e) => setFormData({ ...formData, signal_strength: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    placeholder="dBm"
                  />
                </div>
              </div>

              {(formData.sensor_type === 'rf' || formData.sensor_type === 'radar') && (
                <div>
                  <Label className="text-xs font-mono text-zinc-500 uppercase">Frequency</Label>
                  <Input
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    className="mt-2 bg-zinc-900/50 border-zinc-800 rounded-none"
                    placeholder="e.g., 2.4 GHz"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-sky-600 hover:bg-sky-500 text-white rounded-none uppercase text-xs border border-sky-400/20"
              >
                {submitting ? 'Ingesting...' : 'Ingest Sensor Reading'}
              </Button>
            </form>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-zinc-950/80 border border-emerald-800 rounded-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <Zap className="text-emerald-500" size={24} />
              <h3 className="text-lg font-bold uppercase">AI Fusion Engine</h3>
            </div>
            <p className="text-sm text-zinc-400 mb-4">
              The AI fusion engine automatically correlates multiple sensor readings within 500m radius and fuses them into high-confidence detections.
            </p>
            <Link to="/tracking">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-none uppercase text-xs">
                View Fused Tracking
              </Button>
            </Link>
          </div>

          <div className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-6">
            <h3 className="text-sm font-bold uppercase mb-4 text-zinc-400">Sensor Capabilities</h3>
            <div className="space-y-3 text-xs">
              <div>
                <div className="font-mono text-emerald-500 mb-1">AIR/SURFACE:</div>
                <ul className="list-disc list-inside text-zinc-500 space-y-1">
                  <li>Radar: Position, speed, altitude</li>
                  <li>RF: Signal detection, frequency</li>
                  <li>Optical: Visual confirmation</li>
                </ul>
              </div>
              <div>
                <div className="font-mono text-blue-500 mb-1">UNDERWATER:</div>
                <ul className="list-disc list-inside text-zinc-500 space-y-1">
                  <li>Acoustic: Passive listening</li>
                  <li>Sonar: Active ping detection</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
